/// <amd-module name="Test/TestResultWorkItems/Components/BugsGridView" />
import { CheckboxVisibility, DetailsListLayoutMode, IColumn, Selection, SelectionMode } from 'OfficeFabric/DetailsList';
import { Fabric } from 'OfficeFabric/Fabric';
import * as React from 'react';
import { IViewContextData } from 'Test/common-util/Common';
import { TestCaseResult } from 'Test/common-util/Contracts/Test';
import * as Telemetry from 'Test/common-util/Telemetry';
import { WorkItemsActionsCreator } from 'Test/TestResultWorkItems/Actions/WorkItemsActionCreator';
import { IWorkItemWithColorsAndIcon, WorkItemsActionsHub } from 'Test/TestResultWorkItems/Actions/WorkItemsActionHub';
import { WorkItemFields } from 'Test/TestResultWorkItems/Common';
import * as ConfirmationDialog from 'Test/TestResultWorkItems/Components/ConfirmationDialog';
import * as Resources from 'Test/TestResultWorkItems/Resources';
import * as TelemetryConstants from 'Test/TestResultWorkItems/TelemetryConstants';
import { announce } from 'VSS/Core/Util/Accessibility';
import * as Utils_String from 'VSS/Core/Util/String';
import { ILegacyPlatformService, ILegacyComponentProperties, LegacyComponent } from 'VSS/Legacy/Legacy';
import { Components, IVssComponentContext, IVssComponentProperties, VssComponent, IVssLayoutManager } from 'VSS/Platform/Layout';
import { IVssContextualMenuItem } from 'VSSUI/Components/VssContextualMenu/VssContextualMenu.Props';
import { VssDetailsList } from 'VSSUI/Components/VssDetailsList/VssDetailsList';
import { Tooltip } from 'VSSUI/TooltipEx';
import { css } from 'VSSUI/Util';
import { ago } from 'VSSUI/Utilities/Date';
import { WorkItem } from 'Wit/Clients/Wit/Contracts/WorkItemTracking';

export interface IBugsGridViewProps extends IVssComponentProperties {
    testCaseResult: TestCaseResult;
    viewContext: IViewContextData;
    actionHub: WorkItemsActionsHub;
    actionCreator: WorkItemsActionsCreator;
    workItems: IWorkItemWithColorsAndIcon[];
    hasPublishTestResultsPermission: boolean;
}

export class BugsGridView extends VssComponent<IBugsGridViewProps, {}> {

    private _workItemModified: boolean;
    private _pageContext: IVssComponentContext;
    private _selection: Selection;
    private _selectionDetails: IWorkItemWithColorsAndIcon[];
    constructor(props: any, pageContext: IVssComponentContext) {
        super(props);
        this._pageContext = pageContext;
        this._workItemModified = false;
        this._selection = new Selection({
            onSelectionChanged: () => {
                let previousSelectionLength: number;
                if (this._selectionDetails) {
                    previousSelectionLength = this._selectionDetails.length;
                } else {
                    previousSelectionLength = 0;
                }
                this._selectionDetails = this._getSelectionDetails();
                if (this._selectionDetails.length > previousSelectionLength) {
                    announce(Resources.AnnounceSelected);
                } else {
                    announce(Resources.AnnounceDeselected);
                }
            }
        });
    }

    private _getSelectionDetails(): IWorkItemWithColorsAndIcon[] {
        return (this._selection.getSelection() as IWorkItemWithColorsAndIcon[]);
    }

    public render(): JSX.Element {
        return (
            this._renderWorkItemListElement()
        );
    }

    private _renderWorkItemListElement(): JSX.Element {
        const getMenuItems = (item: IWorkItemWithColorsAndIcon): IVssContextualMenuItem[] => this._createContextMenu(item);

        return (
            <Fabric className="test-bugs-grid-view">
                <h2 className="bugs-header fontWeightNormal">
                    {Utils_String.format(Resources.BugsLabel, (this.props.workItems) ? this.props.workItems.length : 0)}
                </h2>
                {
                    this.props.workItems && this.props.workItems.length > 0 ?
                        <VssDetailsList
                            className="h-scroll-hidden"
                            usePresentationStyles={true}
                            items={this.props.workItems}
                            ariaLabelForGrid={Resources.BugsText}
                            ariaLabelForSelectionColumn={Resources.SelectAll}
                            getRowAriaLabel={(item) => item.name}
                            checkButtonAriaLabel={Resources.SelectRow}
                            compact={true}
                            checkboxVisibility={CheckboxVisibility.onHover}
                            columns={this._columns}
                            layoutMode={DetailsListLayoutMode.justified}
                            isHeaderVisible={true}
                            selection={this._selection}
                            selectionPreservedOnEmptyClick={false}
                            onItemInvoked={(item) => this._onItemInvoked(item, event)}
                            getMenuItems={getMenuItems}
                            actionsColumnKey="title"
                            selectionMode={SelectionMode.multiple}
                        />
                        :
                        null
                }
            </Fabric>
        );
    }

    private _onColumnHeaderClick = (ev: any, column: IColumn) => {
        let newItems: IWorkItemWithColorsAndIcon[] = this.props.workItems.slice();
        let newColumns: IColumn[] = this._columns.slice();
        let currentColumn: IColumn = newColumns.filter((currCol: IColumn, idx: number) => {
            return column.key === currCol.key;
        })[0];
        newColumns.forEach((newColumn: IColumn) => {
            if (newColumn === currentColumn) {
                currentColumn.isSortedDescending = !currentColumn.isSortedDescending;
                currentColumn.isSorted = true;
            } else {
                newColumn.isSorted = false;
            }
        });
        Telemetry.publishEvent(this._pageContext.pageContext, this.props.viewContext, TelemetryConstants.featureBugsGridView_sortOnBugs, {
            "SortedElement": currentColumn.fieldName
        });
        newItems = this.props.actionCreator.sortItems(newItems, currentColumn.fieldName, currentColumn.isSortedDescending);
        this._columns = newColumns;
        this.props.actionCreator.afterSortBugs(newItems);
        if (currentColumn.isSortedDescending) {
            announce(Utils_String.format(Resources.AnnounceSortedDesc, currentColumn.fieldName));
        } else {
            announce(Utils_String.format(Resources.AnnounceSortedAsc, currentColumn.fieldName));
        }
    }

    private _createContextMenu(bug: IWorkItemWithColorsAndIcon): IVssContextualMenuItem[] {
        let items: IVssContextualMenuItem[] = [];

        items.push({
            key: "openBug",
            onClick: () => {
                this._openBug(bug.workItem);
            },
            iconProps: {
                iconName: "ReplyMirrored"
            },
            name: Resources.OpenInNewTab
        });
        if (this.props.hasPublishTestResultsPermission) {
            items.push({
                key: "deleteLink",
                onClick: () => {
                    ConfirmationDialog.openConfirmationDialog(Resources.DeleteRequirementConfirmationText, () => { this._deleteAssociations(); });
                },
                iconProps: {
                    iconName: "ChromeClose",
                },
                name: Resources.RemoveLink
            });
        }
        return items;
    }

    private _onItemInvoked = (item: WorkItem, event: any): void => {
        if (event.type === "keypress" && event.key === "Enter" || event.type === "click") {
            if (event.ctrlKey) {
                this.props.actionCreator.openWorkItem(item);
                return;
            }
            let options = {
                save: (workItem: WorkItem) => {
                    this._workItemModified = true;
                },

                close: (workItem: WorkItem) => {
                    if (this._workItemModified) {
                        this.props.actionCreator.updateBug(workItem);
                    }
                    this._workItemModified = false;
                }
            };

            const legacyService = this.context.pageContext.getService<ILegacyPlatformService>("ILegacyPlatformService");

            legacyService.requireModules(["WorkItemTracking/Scripts/TFS.WorkItemTracking.Controls"]).then(([workItemControlsModule]) => {
                workItemControlsModule.WorkItemFormDialog.showWorkItemById(item.id, this._pageContext, options);
            });
            Telemetry.publishEvent(this._pageContext.pageContext, this.props.viewContext, TelemetryConstants.featureBugsGridView_TestResultViewBug, {
                "ViewAction": "Click on Name",
                "Number of items": 1
            });
        }
    }

    private _openBug(item: WorkItem): void {
        let url = this.props.actionCreator.openWorkItem(item);
        window.open(url, "_blank");
        Telemetry.publishEvent(this._pageContext.pageContext, this.props.viewContext, TelemetryConstants.featureBugsGridView_TestResultViewBug, {
            "ViewAction": "Open on new tab",
            "Number of items": this._selectionDetails.length
        });
    }

    private _deleteAssociations(): void {
        this.props.actionCreator.deleteBugs(this.props.testCaseResult, this._selectionDetails);
        let bugIds: number[] = [];
        this._selectionDetails.forEach((bug) => {
            bugIds.push(bug.workItem.id);
        });
        const layoutManager = this._pageContext.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss: any) => {
            const properties: ILegacyComponentProperties = {
                modules: ["TestManagement/Scripts/Shared/RemoveWorkItemsProxy"],
                wrappedType: "remove-work-item-proxy",
                wrappedProps: { workItemIds: bugIds }
            };
            return React.createElement(LegacyComponent, properties);
        });
        this._selection.setAllSelected(false);
        Telemetry.publishEvent(this._pageContext.pageContext, this.props._viewContext, TelemetryConstants.featureBugsGridView_RemoveAssociation_Bugs, {
            "Number of items": this._selectionDetails.length
        });

    }

    private _columns: IColumn[] = [
        {
            key: "bugId",
            name: Resources.TestPointGridColumnID,
            fieldName: "id",
            minWidth: 20,
            maxWidth: 40,
            isResizable: true,
            isSorted: true,
            isSortedDescending: true,
            onColumnClick: this._onColumnHeaderClick,
            data: "string",
            onRender: (item: IWorkItemWithColorsAndIcon, index: number) => {
                return (
                    <div>
                        <span>
                            {item.workItem.id}
                        </span>
                    </div>
                );
            }
        },
        {
            key: "title",
            name: Resources.WorkItemGridTitleColumnHeader,
            fieldName: WorkItemFields.Title,
            minWidth: 300,
            maxWidth: 450,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            onColumnClick: this._onColumnHeaderClick,
            data: "string",
            onRender: (item: IWorkItemWithColorsAndIcon, index: number) => {
                return (
                    <div className="workItem-icon-and-name flex">
                        <span
                            className={css("bowtie-icon bug-symbol-styling", item.icon)}
                            style={{ color: item.color.startsWith("#") ? item.color : "#" + item.color }}>
                        </span>
                        <div className="bug-name text-ellipsis">
                            <Tooltip text={item.workItem.fields[WorkItemFields.Title]}>
                                <span>
                                    <a role="link"
                                        onKeyPress={(event) => this._onItemInvoked(item.workItem, event)}
                                        onClick={(event) => this._onItemInvoked(item.workItem, event)}>
                                        {item.workItem.fields[WorkItemFields.Title]}
                                    </a>
                                </span>
                            </Tooltip>
                        </div>
                    </div>
                );
            }
        },
        {
            key: "state",
            name: Resources.ExploratorySessionGridHeader_State,
            fieldName: WorkItemFields.State,
            minWidth: 80,
            maxWidth: 100,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            onColumnClick: this._onColumnHeaderClick,
            data: "string",
            onRender: (item: IWorkItemWithColorsAndIcon, index: number) => {
                return (
                    <div>
                        <Tooltip text={item.workItem.fields[WorkItemFields.State]}>
                            <span>
                                <span className="workitem-state-circle" style={{ backgroundColor: "#" + item.stateColor, borderColor: "#" + item.stateColor }}></span>
                                <span>{item.workItem.fields[WorkItemFields.State]}</span>
                            </span>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            key: "assignedTo",
            name: Resources.AssignedTo,
            fieldName: WorkItemFields.AssignedTo,
            minWidth: 150,
            maxWidth: 200,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            onColumnClick: this._onColumnHeaderClick,
            data: "string",
            onRender: (item: IWorkItemWithColorsAndIcon, index: number) => {
                const assignedTo: string = item.workItem.fields[WorkItemFields.AssignedTo] ? item.workItem.fields[WorkItemFields.AssignedTo].displayName : "";
                return (
                    <div>
                        <Tooltip text={assignedTo}>
                            <span>
                                {assignedTo}
                            </span>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            key: "createdDate",
            name: Resources.DateCreated,
            fieldName: WorkItemFields.CreatedDate,
            minWidth: 125,
            maxWidth: 175,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            onColumnClick: this._onColumnHeaderClick,
            data: "string",
            onRender: (item: IWorkItemWithColorsAndIcon, index: number) => {
                return (
                    <div>
                        <Tooltip text={new Date(item.workItem.fields[WorkItemFields.CreatedDate]).toString()}>
                            <span>
                                {ago(new Date(item.workItem.fields[WorkItemFields.CreatedDate]))}
                            </span>
                        </Tooltip>
                    </div>
                );
            }
        },
        {
            key: "lastUpdated",
            name: Resources.LastUpdated,
            fieldName: WorkItemFields.ChangedDate,
            minWidth: 125,
            maxWidth: 175,
            isResizable: true,
            isSorted: false,
            isSortedDescending: false,
            onColumnClick: this._onColumnHeaderClick,
            data: "string",
            onRender: (item: IWorkItemWithColorsAndIcon, index: number) => {
                return (
                    <div>
                        <Tooltip text={new Date(item.workItem.fields[WorkItemFields.ChangedDate]).toString()}>
                            <span>
                                {ago(new Date(item.workItem.fields[WorkItemFields.ChangedDate]))}
                            </span>
                        </Tooltip>
                    </div>
                );
            }
        }
    ];

}

Components.add("test-resultBugsView", BugsGridView);